<script setup lang="ts">
/**
 * UDrawer · 右侧滑出抽屉（标准组件）
 * ------------------------------------------------------------
 * 分场景约定：短表单的新增/编辑用居中 Modal（sa-modal）；
 * 多字段表单、明细行编辑、详情速览用本抽屉。
 * 用法：
 *   <UDrawer :open="x" title="标题" width="520px" @close="x = false">
 *     主体内容…
 *     <template #footer><UButton …/></template>
 *   </UDrawer>
 * 交互：遮罩点击 / Esc / 右上角 × 关闭；header、footer 均可省略。
 */
import { onUnmounted, watch } from 'vue'

const props = withDefaults(defineProps<{
  open: boolean
  title?: string
  /** 抽屉宽度（px / rem 均可），默认 480px */
  width?: string
}>(), { title: '', width: '480px' })

const emit = defineEmits<{ (e: 'close'): void }>()

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.open) emit('close')
}
watch(() => props.open, v => {
  if (v) document.addEventListener('keydown', onKey)
  else document.removeEventListener('keydown', onKey)
})
onUnmounted(() => document.removeEventListener('keydown', onKey))
</script>

<template>
  <Teleport to="body">
    <div v-if="open">
      <div class="ud-mask" @click="emit('close')" />
      <aside class="udrawer" :style="{ width }" role="dialog" aria-modal="true" :aria-label="title || '抽屉'">
        <header v-if="title || $slots.header" class="ud-h">
          <slot name="header"><b>{{ title }}</b></slot>
          <button class="ud-x" aria-label="关闭" @click="emit('close')">×</button>
        </header>
        <div class="ud-b"><slot /></div>
        <footer v-if="$slots.footer" class="ud-f"><slot name="footer" /></footer>
      </aside>
    </div>
  </Teleport>
</template>

<style scoped>
.ud-mask { position: fixed; inset: 0; z-index: 700; background: rgba(20, 22, 26, .35); animation: udFade .18s ease; }
html[data-mode="dark"] .ud-mask { background: rgba(0, 0, 0, .55); }
.udrawer {
  position: fixed; z-index: 701; top: 0; right: 0; height: 100%; max-width: 92vw;
  background: var(--surface); border-left: 1px solid var(--line); box-shadow: var(--sh-4);
  display: flex; flex-direction: column; animation: udIn .22s ease;
}
@keyframes udIn { from { transform: translateX(36px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes udFade { from { opacity: 0; } to { opacity: 1; } }
.ud-h { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 18px; border-bottom: 1px solid var(--line-soft); flex: none; }
.ud-h b { font-size: var(--fs-md, 14px); color: var(--ink); }
.ud-x { width: 26px; height: 26px; border: none; border-radius: 7px; font-size: 17px; color: var(--ink-4); background: none; cursor: pointer; line-height: 1; }
.ud-x:hover { background: var(--surface-3); color: var(--ink); }
.ud-b { padding: 16px 18px; overflow: auto; flex: 1; min-height: 0; }
.ud-f { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid var(--line-soft); flex: none; }
</style>
